// Builds the Verkada stream URL from the parts POST /cameras/:deviceID/stream-token signs.
// The browser decides codec, resolution and window; changing one must not need a deploy.

/**
 * `transcode` is deliberately never sent — Verkada's docs mark it pre-loaded into the
 * playlist's segment urls. Asking for h264 is how a caller requests it; hevc needs none,
 * but plays only where the browser decodes HEVC in MSE (Safari, capable Chrome/Edge).
 */
export const LIVE_CODEC = Object.freeze({
	compatible: 'h264',
	native: 'hevc'
});

/**
 * The playable m3u8.
 *
 * @param {Object} params
 * @param {string} params.baseUrl - region host, resolved server-side
 * @param {string} params.verkadaOrgID
 * @param {string} params.cameraID - the VENDOR's id, never our deviceID
 * @param {string} params.jwt
 * @param {number|null} [params.from] - epoch seconds; null/0 is live
 * @param {number|null} [params.to]
 * @param {('low_res'|'high_res')} [params.resolution] - a quality choice on live; on
 *   history a prioritisation hint, where low_res means "only low resolution footage"
 * @param {string} [params.codec] - see LIVE_CODEC
 * @returns {string}
 */
export const buildVerkadaStreamUrl = ({
	baseUrl,
	verkadaOrgID,
	cameraID,
	jwt,
	from = null,
	to = null,
	resolution = 'low_res',
	codec = LIVE_CODEC.compatible
}) => {
	const url = new URL(`${baseUrl}/stream/cameras/v1/footage/stream/stream.m3u8`);
	url.searchParams.set('org_id', verkadaOrgID);
	url.searchParams.set('camera_id', cameraID);
	url.searchParams.set('jwt', jwt);
	// 0/0 selects live. `type` is omitted: the docs call it reserved, and it defaults here.
	url.searchParams.set('start_time', String(from ?? 0));
	url.searchParams.set('end_time', String(to ?? 0));
	url.searchParams.set('codec', codec);
	url.searchParams.set('resolution', resolution);
	return url.toString();
};
